import { ScriptableContext } from "chart.js";
import { useMemo } from "react";
import { getSingleGroupingDataVisualizationColors } from "../../../theme/colors";
import { ChartProcessedData, ChartSeries, RedoChartStyle } from "./types";

export const useChartData = (
  series: ChartSeries[],
  chartStyle: RedoChartStyle,
  dateFormatter: (date: Temporal.Instant) => string,
  disableGradient: boolean = false,
): ChartProcessedData => {
  return useMemo(() => {
    const colors = getSingleGroupingDataVisualizationColors(series.length);

    const coloredSeries = series.map((s, i) => ({
      ...s,
      color: s.color || colors[i],
    }));

    const dateMap = new Map<
      string,
      { formatted: string; instant: Temporal.Instant }
    >();

    for (const s of coloredSeries) {
      for (const { date } of s.data) {
        const key = date.toString();
        if (!dateMap.has(key)) {
          dateMap.set(key, { formatted: dateFormatter(date), instant: date });
        }
      }
    }

    const dateEntries = [...dateMap.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => ({ key, ...val }));

    const labels = dateEntries.map((d) => d.formatted);

    const normalizedSeries = coloredSeries.map((s) => {
      const valueByDate = new Map(
        s.data.map((d) => [d.date.toString(), d.value]),
      );

      const normalizedData = dateEntries.map((d) => ({
        date: d.instant,
        value: valueByDate.get(d.key) ?? 0,
      }));

      return { ...s, data: normalizedData };
    });

    const datasets = normalizedSeries.map((s) => ({
      label: s.name,
      data: s.data.map((d) => d.value),
      backgroundColor: (ctx: ScriptableContext<"bar" | "line">) => {
        if (chartStyle !== "line" || disableGradient) return s.color;

        const { chartArea, ctx: canvas } = ctx.chart;
        if (!chartArea) return s.color;

        const gradient = canvas.createLinearGradient(
          0,
          chartArea.top,
          0,
          chartArea.bottom,
        );
        gradient.addColorStop(0, `${s.color}40`);
        gradient.addColorStop(1, `${s.color}00`);
        return gradient;
      },
      borderColor: s.color,
      borderWidth: chartStyle === "line" ? 1.5 : undefined,
      tension: chartStyle === "line" ? 0.4 : undefined,
      pointRadius: chartStyle === "line" ? 0 : undefined,
      fill: chartStyle === "line" && !disableGradient,
      borderRadius: chartStyle === "bar" ? 4 : undefined,
    }));

    return { labels, datasets, normalizedSeries };
  }, [series, chartStyle, dateFormatter, disableGradient]);
};
